import {
  describeRule, repeatKindOf, ruleForKind, slugifyTag, weekdaysOfRule,
  type IsoDate, type QuickAdd, type RepeatKind, type Task, type User,
} from '@hestia/core';

/** Everything a task can be, in the shape the composer edits it.
 *
 *  One model for creating and for editing. Those used to be different screens
 *  with different powers — the edit screen could change a title and nothing else
 *  — and there was never a reason for it beyond how they grew. */
export interface Draft {
  title: string;
  notes: string;
  date: IsoDate;
  /** The last day, when the task is on for more than one. Null is the ordinary
   *  case. Never set alongside a repeat — the two are mutually exclusive. */
  endDate: IsoDate | null;
  /** null means the task belongs to a day, not to a minute — the common case. */
  time: string | null;
  durationMin: number | null;
  assigneeId: string | null;
  repeat: RepeatKind;
  /** 0 = Monday … 6 = Sunday. Only meaningful when `repeat` is weekly. */
  weekdays: number[];
  /** Tag slugs. Ones that do not exist yet are created on save. */
  tags: string[];
}

/** The attributes a person can set by hand, and therefore the ones the sentence
 *  they are typing must not take back off them. */
export type Field = 'date' | 'time' | 'duration' | 'repeat' | 'assignee' | 'tags';

export const emptyDraft = (today: IsoDate): Draft => ({
  title: '', notes: '', date: today, endDate: null, time: null, durationMin: null,
  assigneeId: null, repeat: 'none', weekdays: [], tags: [],
});

export const draftOfTask = (task: Task, tagSlugs: string[]): Draft => ({
  title: task.title,
  notes: task.notes ?? '',
  date: task.startDate,
  endDate: task.endDate,
  time: task.startTime,
  durationMin: task.durationMin,
  assigneeId: task.assigneeId,
  repeat: repeatKindOf(task.rrule),
  weekdays: weekdaysOfRule(task.rrule),
  tags: tagSlugs,
});

/** The rule a draft means. A repeat the buttons cannot describe is left exactly
 *  as it was found — the composer will not quietly turn someone's "every other
 *  Tuesday" into "weekly" just because it has no button for it. */
export const ruleOfDraft = (d: Draft, original: string | null): string | null =>
  // A span never repeats; the server refuses the pair, so do not offer it one.
  d.endDate ? null : d.repeat === 'custom' ? original : ruleForKind(d.repeat, d.weekdays);

export const repeatLabel = (d: Draft, original: string | null): string =>
  describeRule(ruleOfDraft(d, original), { short: true });

/** What a half-typed line of plain English means for the draft behind it.
 *
 *  Only what the sentence actually says is applied, and only to attributes the
 *  person has not already set themselves. Tapping a date and then carrying on
 *  typing must not drag the task back to today, and quick-add always resolves a
 *  date whether or not the words named one — hence `matchedDate`. */
export function applyParse(
  d: Draft, p: QuickAdd, users: User[], touched: ReadonlySet<Field>,
): Draft {
  const free = (f: Field) => !touched.has(f);
  const next: Draft = { ...d, title: p.title };
  if (p.matchedDate && free('date')) next.date = p.startDate;
  if (p.startTime && free('time')) next.time = p.startTime;
  if (p.durationMin && free('duration')) next.durationMin = p.durationMin;
  if (p.rrule && free('repeat')) {
    next.repeat = repeatKindOf(p.rrule);
    next.weekdays = weekdaysOfRule(p.rrule);
  }
  if (p.assigneeHint && free('assignee')) {
    const u = users.find((x) => x.username === p.assigneeHint
      || x.displayName.toLowerCase() === p.assigneeHint);
    if (u) next.assigneeId = u.id;
  }
  if (p.tagHints.length && free('tags')) {
    next.tags = [...new Set([...d.tags, ...p.tagHints.map(slugifyTag).filter(Boolean)])];
  }
  return next;
}

/** Whether two drafts differ, so an edit that changed nothing sends nothing. */
export function sameDraft(a: Draft, b: Draft): boolean {
  return a.title === b.title && a.notes === b.notes && a.date === b.date
    && a.endDate === b.endDate
    && a.time === b.time && a.durationMin === b.durationMin
    && a.assigneeId === b.assigneeId && a.repeat === b.repeat
    && a.weekdays.join() === b.weekdays.join()
    && [...a.tags].sort().join() === [...b.tags].sort().join();
}
